import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { EditOutlined, PlusOutlined, ReloadOutlined, StopOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  CityNotice,
  CityNoticePayload,
  NoticeGeom,
  NOTICE_KIND_LABELS,
  NOTICE_SEVERITY_LABELS,
  NoticeKind,
  NoticeSeverity,
  cityNoticesApi,
} from '../../api/cityNotices';
import { PageHeader } from '../../components/shared';
import NoticeGeomEditor from './NoticeGeomEditor';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

/**
 * ГОРОДСКИЕ УВЕДОМЛЕНИЯ — то, что житель видит ПЕРВЫМ в блоке «Сегодня в
 * городе»: перекрыли дорогу, объявили актированный день, ввели ограничение.
 * Заведено 18.08.2026 по замыслу владельца.
 *
 * Отличие от «Отключений»: там ЖКХ (вода/свет/газ/тепло) с адресами и
 * рассылкой, здесь — городские события со СРОКОМ ДЕЙСТВИЯ и степенью
 * важности. Пока уведомление действует, приложение его показывает; кончился
 * срок — исчезает само, снимать руками не нужно.
 *
 * «Снять» НЕ удаляет строку, а гасит показ: у уведомления есть срок и
 * адресаты, и «кто что объявлял» — часть городской истории.
 */

const SEVERITY_COLOR: Record<NoticeSeverity, string> = {
  critical: 'red',
  warning: 'orange',
  info: 'blue',
};

export default function CityNoticesPage() {
  const [items, setItems] = useState<CityNotice[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CityNotice | null>(null);
  /**
   * Геометрия живёт СВОИМ состоянием, а не полем формы: её меняет карта, а не
   * ввод, и `Form.Item` тут дал бы второй источник правды между кликами по
   * карте и значением формы.
   */
  const [geom, setGeom] = useState<NoticeGeom>(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await cityNoticesApi.list({ include_inactive: true, size: 100 });
      setItems(data.items);
    } catch {
      message.error('Не удалось загрузить уведомления');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ kind: 'road_closed', severity: 'warning' });
    setGeom(null);
    setModalOpen(true);
  };

  const openEdit = (n: CityNotice) => {
    setEditing(n);
    form.setFieldsValue({
      ...n,
      period: [dayjs(n.starts_at), n.ends_at ? dayjs(n.ends_at) : null],
      addresses: (n.affected_addresses || []).join('\n'),
    });
    setGeom(n.geom ?? null);
    setModalOpen(true);
  };

  const submit = async () => {
    const v = await form.validateFields();
    // Пустой конец периода = «до отмены»: так уведомление держится, пока его
    // не снимут (карантин, перекрытие без обещанной даты).
    const [from, to] = v.period || [];
    const payload: CityNoticePayload = {
      kind: v.kind,
      severity: v.severity,
      title_ru: v.title_ru,
      title_kk: v.title_kk || undefined,
      body_ru: v.body_ru || undefined,
      body_kk: v.body_kk || undefined,
      affected_addresses: v.addresses
        ? String(v.addresses)
            .split('\n')
            .map((s: string) => s.trim())
            .filter(Boolean)
        : undefined,
      starts_at: from.toISOString(),
      ends_at: to ? to.toISOString() : null,
      // Всегда отправляем: стёртая на карте линия обязана стереться и в базе.
      geom,
    };
    try {
      if (editing) {
        await cityNoticesApi.update(editing.id, payload);
        message.success('Уведомление обновлено');
      } else {
        await cityNoticesApi.create(payload);
        message.success('Уведомление опубликовано');
      }
      setModalOpen(false);
      load();
    } catch {
      message.error('Не удалось сохранить');
    }
  };

  const deactivate = async (id: number) => {
    try {
      await cityNoticesApi.deactivate(id);
      message.success('Снято с показа');
      load();
    } catch {
      message.error('Не удалось снять');
    }
  };

  const columns: ColumnsType<CityNotice> = [
    {
      title: 'Что',
      dataIndex: 'title_ru',
      render: (title: string, n) => (
        <Space direction="vertical" size={2}>
          <span style={{ fontWeight: 600 }}>{title}</span>
          <Space size={4}>
            <Tag>{NOTICE_KIND_LABELS[n.kind] || n.kind}</Tag>
            <Tag color={SEVERITY_COLOR[n.severity]}>{NOTICE_SEVERITY_LABELS[n.severity]}</Tag>
            {!n.is_active && <Tag color="default">Снято</Tag>}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Когда действует',
      render: (_, n) => (
        <Space direction="vertical" size={0}>
          <span>{dayjs(n.starts_at).format('DD.MM.YYYY HH:mm')}</span>
          <span style={{ color: '#888' }}>
            {n.ends_at ? `до ${dayjs(n.ends_at).format('DD.MM.YYYY HH:mm')}` : 'до отмены'}
          </span>
        </Space>
      ),
      width: 210,
    },
    {
      title: 'Где',
      render: (_, n) => (
        <Space direction="vertical" size={2}>
          <span>{n.affected_addresses?.length ? n.affected_addresses.join('; ') : 'весь город'}</span>
          {/* Нарисована ли зона — видно из списка: иначе понять это можно было
              бы только открыв запись, а рисуют её не всегда и не все. */}
          {n.geom ? <Tag>на карте</Tag> : null}
        </Space>
      ),
    },
    {
      title: '',
      width: 130,
      render: (_, n) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(n)} />
          {n.is_active && (
            <Popconfirm title="Снять с показа?" onConfirm={() => deactivate(n.id)}>
              <Button size="small" danger icon={<StopOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Городские уведомления"
        subtitle="Перекрытия дорог, актированные дни, ограничения — показываются первыми в блоке «Сегодня в городе»"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} />
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Опубликовать
            </Button>
          </Space>
        }
      />

      <Card>
        <Table rowKey="id" columns={columns} dataSource={items} loading={loading} pagination={{ pageSize: 20 }} />
      </Card>

      <Modal
        title={editing ? 'Изменить уведомление' : 'Новое уведомление'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        okText="Сохранить"
        width={640}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="kind" label="Что произошло" rules={[{ required: true }]}>
            <Select
              options={(Object.keys(NOTICE_KIND_LABELS) as NoticeKind[]).map((k) => ({
                value: k,
                label: NOTICE_KIND_LABELS[k],
              }))}
            />
          </Form.Item>
          <Form.Item name="severity" label="Важность" rules={[{ required: true }]}>
            <Select
              options={(Object.keys(NOTICE_SEVERITY_LABELS) as NoticeSeverity[]).map((s) => ({
                value: s,
                label: NOTICE_SEVERITY_LABELS[s],
              }))}
            />
          </Form.Item>
          <Form.Item
            name="title_ru"
            label="Заголовок (рус)"
            rules={[{ required: true, min: 3 }]}
            extra="Одной фразой, как житель прочитает это в списке"
          >
            <Input placeholder="Ограничение движения на улице Дружбы Народов" />
          </Form.Item>
          <Form.Item name="title_kk" label="Заголовок (каз)">
            <Input />
          </Form.Item>
          <Form.Item name="body_ru" label="Подробности (рус)">
            <TextArea rows={3} placeholder="Причина, объезд, до какого числа" />
          </Form.Item>
          <Form.Item name="body_kk" label="Подробности (каз)">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="period"
            label="Срок действия"
            rules={[{ required: true, message: 'Укажите хотя бы начало' }]}
            extra="Конец можно не указывать — тогда уведомление держится до снятия"
          >
            <RangePicker showTime format="DD.MM.YYYY HH:mm" allowEmpty={[false, true]} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="addresses" label="Адреса" extra="По одному в строке. Пусто — касается всего города">
            <TextArea rows={2} placeholder="улица Дружбы Народов, участок от 1 до 15" />
          </Form.Item>
          {/* ── ГДЕ ЭТО НА КАРТЕ (Заход 4) ─────────────────────────────────
              Адреса словами остаются: их читает и Айлана, и список. Линия —
              для жителя, который по строке «участок от 1 до 15» не понимает,
              попадает ли его двор. */}
          <Form.Item
            label="Где это на карте"
            extra="Кликайте по карте: линия — перекрытый участок, зона — полигон ограничения. Можно не рисовать."
          >
            {/* Ключ по редактируемой записи: без него редактор оставлял бы
                точки прошлого уведомления при открытии следующего. */}
            <NoticeGeomEditor key={editing?.id ?? 'new'} value={geom} onChange={setGeom} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
